import { useEffect, useRef, useState, useCallback } from 'react'
import { Html } from '@react-three/drei'
import { useBookStore } from '../../stores'
import { ttsPlayer, howlPlayer } from '../../lib/audio/ttsPlayer'

interface Timestamp {
  word: string
  start: number
  end: number
}

interface DialogueSegment {
  speaker: string
  text: string
}

interface StoryTextProps {
  text?: string
  dialogueSegments?: DialogueSegment[]
  position: [number, number, number]
  audioUrl?: string
  timestamps?: Timestamp[]
  voiceMap?: Record<string, { rate?: number; pitch?: number; volume?: number; lang?: string; color?: string }>
}

/** P5-01 默认角色颜色映射 */
const DEFAULT_SPEAKER_COLORS: Record<string, string> = {
  narrator: '#2c1810',    // 深褐（叙述者默认）
  旁白: '#2c1810',
  小明: '#2563eb',       // 蓝
  小红: '#dc2626',       // 红
  老师: '#059669',       // 绿
  妈妈: '#7c3aed',       // 紫
  爸爸: '#ea580c',       // 橙
  猫: '#f59e0b',         // 琥珀
  狗: '#8b5cf6',         // 紫罗兰
}

export default function StoryText({
  text,
  dialogueSegments,
  position,
  audioUrl,
  timestamps: propTimestamps,
  voiceMap = {}
}: StoryTextProps) {
  const [words, setWords] = useState<{ word: string; speaker: string }[]>([])
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [currentSpeaker, setCurrentSpeaker] = useState<string | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingResult, setRecordingResult] = useState<string | null>(null)
  const { currentWordIndex, ttsTimestamps: storeTimestamps } = useBookStore()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const speakingRef = useRef(false)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordedChunksRef = useRef<Blob[]>([])

  const timestamps = propTimestamps ?? storeTimestamps

  // 构建带角色标记的词列表
  useEffect(() => {
    if (dialogueSegments && dialogueSegments.length > 0) {
      const result: { word: string; speaker: string }[] = []
      for (const seg of dialogueSegments) {
        const segWords = tokenize(seg.text)
        for (const w of segWords) {
          result.push({ word: w, speaker: seg.speaker })
        }
      }
      setWords(result)
    } else if (text) {
      const segWords = tokenize(text)
      setWords(segWords.map((w) => ({ word: w, speaker: 'narrator' })))
    }
  }, [text, dialogueSegments])

  useEffect(() => {
    setHighlightedIndex(currentWordIndex)
  }, [currentWordIndex])

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current
    if (!audio || !timestamps || timestamps.length === 0) return
    const currentTime = audio.currentTime * 1000
    const idx = timestamps.findIndex(
      (t) => currentTime >= t.start && currentTime <= t.end
    )
    setHighlightedIndex(idx)
    if (idx >= 0 && idx < words.length) {
      setCurrentSpeaker(words[idx].speaker)
    }
  }, [timestamps, words])

  const handleEnded = useCallback(() => {
    setHighlightedIndex(-1)
    setCurrentSpeaker(null)
    speakingRef.current = false
  }, [])

  const handlePlay = useCallback(() => {
    // 优先使用后端预生成音频
    if (audioUrl && timestamps && timestamps.length > 0) {
      howlPlayer.play(audioUrl, {
        onWord: (_word: string, index: number) => {
          setHighlightedIndex(index)
          if (index >= 0 && index < words.length) {
            setCurrentSpeaker(words[index].speaker)
          }
        },
        onEnd: () => {
          setHighlightedIndex(-1)
          setCurrentSpeaker(null)
          speakingRef.current = false
        },
        timestamps,
      })
      speakingRef.current = true
      return
    }

    // 多角色 TTS 播放
    if (dialogueSegments && dialogueSegments.length > 0 && ttsPlayer.isSupported) {
      const segments = dialogueSegments.map((seg) => ({
        speaker: seg.speaker,
        text: seg.text,
      }))
      const vm: Record<string, any> = {}
      for (const [name, cfg] of Object.entries(voiceMap)) {
        vm[name] = { rate: cfg.rate, pitch: cfg.pitch, volume: cfg.volume, lang: cfg.lang }
      }

      ttsPlayer.speakMultiRole(segments, vm, {
        onWord: (_word: string, index: number, _startMs: number, _endMs: number, speaker: string) => {
          setHighlightedIndex(index)
          setCurrentSpeaker(speaker)
        },
        onSegmentStart: (speaker: string) => {
          setCurrentSpeaker(speaker)
        },
        onSegmentEnd: () => {
          setCurrentSpeaker(null)
        },
        onComplete: () => {
          setHighlightedIndex(-1)
          setCurrentSpeaker(null)
          speakingRef.current = false
        },
      })
      speakingRef.current = true
      return
    }

    // 降级到单声道 TTS
    if (ttsPlayer.isSupported && text) {
      ttsPlayer.speak(text, {
        onWord: (_word: string, index: number) => {
          setHighlightedIndex(index)
        },
        onEnd: () => {
          setHighlightedIndex(-1)
          speakingRef.current = false
        },
      })
      speakingRef.current = true
    }
  }, [audioUrl, timestamps, dialogueSegments, text, voiceMap, words])

  const handleStop = useCallback(() => {
    ttsPlayer.stop()
    howlPlayer.stop()
    setHighlightedIndex(-1)
    setCurrentSpeaker(null)
    speakingRef.current = false
    setIsRecording(false)
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
      mediaRecorderRef.current = null
    }
  }, [])

  /** P5-01: 跟读评分 — 录音 */
  const handleRecord = useCallback(async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop()
      setIsRecording(false)
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      recordedChunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm' })
        const url = URL.createObjectURL(blob)
        setRecordingResult(url)
        stream.getTracks().forEach((t) => t.stop())
      }

      recorder.start()
      mediaRecorderRef.current = recorder
      setIsRecording(true)
      setRecordingResult(null)
    } catch (err) {
      console.warn('录音启动失败:', err)
      alert('无法访问麦克风，请检查权限设置')
    }
  }, [isRecording])

  const handlePlayRecording = useCallback(() => {
    if (!recordingResult) return
    const audio = new Audio(recordingResult)
    audio.play()
  }, [recordingResult])

  const isSpeaking = speakingRef.current

  return (
    <Html
      position={position}
      transform
      occlude
      style={{
        width: '600px',
        textAlign: 'center',
        pointerEvents: 'none',
        userSelect: 'none'
      }}
    >
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          style={{ display: 'none' }}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
        />
      )}

      {/* 角色指示器 */}
      {currentSpeaker && currentSpeaker !== 'narrator' && currentSpeaker !== '旁白' && (
        <div style={{
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: '600',
          color: getSpeakerColor(currentSpeaker),
          background: 'rgba(255,255,255,0.8)',
          padding: '4px 12px',
          borderRadius: '12px',
          display: 'inline-block',
        }}>
          {currentSpeaker}
        </div>
      )}

      <div
        style={{
          fontSize: '28px',
          fontFamily: '"Noto Sans SC", "PingFang SC", sans-serif',
          lineHeight: 1.8,
          color: '#2c1810',
          textShadow: '0 2px 4px rgba(255,255,255,0.5)',
          background: 'rgba(255, 248, 235, 0.85)',
          padding: '20px 30px',
          borderRadius: '12px',
          backdropFilter: 'blur(4px)'
        }}
      >
        {words.map((item, index) => {
          const isHighlighted = index === highlightedIndex
          const speakerColor = getSpeakerColor(item.speaker)
          return (
            <span
              key={index}
              style={{
                display: 'inline-block',
                padding: '2px 4px',
                margin: '0 1px',
                borderRadius: '4px',
                transition: 'all 0.15s ease-out',
                transform: isHighlighted ? 'scale(1.08)' : 'scale(1)',
                backgroundColor: isHighlighted
                  ? `${speakerColor}66` // 40% opacity
                  : 'transparent',
                color: isHighlighted ? speakerColor : '#2c1810',
                fontWeight: isHighlighted ? '700' : '400',
                textDecoration: item.speaker !== 'narrator' && item.speaker !== '旁白'
                  ? 'underline dotted'
                  : 'none',
                textDecorationColor: isHighlighted ? speakerColor : 'transparent',
              }}
            >
              {item.word}
            </span>
          )
        })}
      </div>

      {/* 播放控制区 */}
      <div style={{ marginTop: '12px', pointerEvents: 'auto', display: 'flex', justifyContent: 'center', gap: '8px' }}>
        {isSpeaking ? (
          <button
            onClick={handleStop}
            style={{
              padding: '8px 20px',
              fontSize: '16px',
              fontFamily: '"Noto Sans SC", "PingFang SC", sans-serif',
              background: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            停止
          </button>
        ) : (
          <button
            onClick={handlePlay}
            style={{
              padding: '8px 20px',
              fontSize: '16px',
              fontFamily: '"Noto Sans SC", "PingFang SC", sans-serif',
              background: '#fbbf24',
              color: '#92400e',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            {audioUrl ? '播放配音' : dialogueSegments ? '多角色朗读' : '朗读'}
          </button>
        )}

        {/* 跟读录音按钮 */}
        <button
          onClick={handleRecord}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            fontFamily: '"Noto Sans SC", "PingFang SC", sans-serif',
            background: isRecording ? '#fca5a5' : '#e0e7ff',
            color: isRecording ? '#dc2626' : '#4338ca',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {isRecording ? '● 录音中...' : '🎙 跟读'}
        </button>
      </div>

      {/* 录音回放 */}
      {recordingResult && !isRecording && (
        <div style={{ marginTop: '8px' }}>
          <button
            onClick={handlePlayRecording}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              background: '#dcfce7',
              color: '#166534',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            ▶ 回放我的朗读
          </button>
        </div>
      )}
    </Html>
  )
}

/** 获取角色颜色 */
function getSpeakerColor(speaker: string): string {
  return DEFAULT_SPEAKER_COLORS[speaker] || '#2c1810'
}

/** 分词：中文按字，英文按空格 */
function tokenize(text: string): string[] {
  const isChinese = (char: string) => /\p{Script=Han}/u.test(char)
  const result: string[] = []
  let current = ''
  for (const char of text) {
    if (isChinese(char)) {
      if (current) {
        result.push(...current.trim().split(/\s+/).filter(Boolean))
        current = ''
      }
      result.push(char)
    } else if (/\s/.test(char)) {
      if (current) {
        result.push(...current.trim().split(/\s+/).filter(Boolean))
        current = ''
      }
    } else {
      current += char
    }
  }
  if (current) {
    result.push(...current.trim().split(/\s+/).filter(Boolean))
  }
  return result
}
